import { AxiosError } from 'axios';

import { apiClient } from './apiClient';

/**
 * Normalize FastAPI `detail` for display: string, Pydantic validation array (`{ msg, loc }[]`),
 * or a single object with `msg`.
 */
function formatFastApiDetail(detail: unknown): string | null {
  if (detail == null) return null;

  if (typeof detail === 'string') {
    const t = detail.trim();
    return t.length > 0 ? t : null;
  }

  if (Array.isArray(detail)) {
    const parts: string[] = [];
    for (const item of detail) {
      if (typeof item === 'string') {
        const s = item.trim();
        if (s) parts.push(s);
        continue;
      }
      if (item && typeof item === 'object' && 'msg' in item) {
        const row = item as { msg?: unknown; loc?: unknown };
        const msg = row.msg;
        if (typeof msg === 'string' && msg.trim()) {
          const loc = row.loc;
          const locStr =
            Array.isArray(loc) && loc.length > 0
              ? loc
                  .map((x) => String(x))
                  .filter(Boolean)
                  .join('.')
              : null;
          parts.push(locStr ? `${locStr}: ${msg.trim()}` : msg.trim());
        }
      }
    }
    if (parts.length > 0) {
      return parts.join('; ');
    }
    if (detail.length > 0) {
      try {
        return JSON.stringify(detail);
      } catch {
        return null;
      }
    }
    return null;
  }

  if (typeof detail === 'object') {
    const obj = detail as Record<string, unknown>;
    if (typeof obj.msg === 'string' && obj.msg.trim()) {
      return obj.msg.trim();
    }
    try {
      return JSON.stringify(detail);
    } catch {
      return null;
    }
  }

  return String(detail);
}

/** Prefer FastAPI `detail` (string or validation array) when axios rejects (4xx/5xx). */
export function getWorkspaceApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError && error.response?.data != null) {
    const data = error.response.data as { detail?: unknown };
    const fromDetail = formatFastApiDetail(data.detail);
    if (fromDetail) {
      return fromDetail;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

const WORKSPACE_CREATE_GENERIC = "We couldn't create your workspace. Please try again.";

/**
 * Stable, customer-facing copy for workspace creation failures (modal / UI).
 * Avoids raw axios messages, stack traces, and JSON dumps.
 */
export function getWorkspaceCreationUserFacingMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const data = error.response?.data as { detail?: unknown } | undefined;

    if (status === 401) {
      return 'Please sign in again to create a workspace.';
    }
    if (status === 403) {
      return "You don't have permission to create a workspace.";
    }
    if (status === 409) {
      return 'A workspace with that name may already exist. Try a different name.';
    }
    if (status === 422) {
      const fromDetail = data != null ? formatFastApiDetail(data.detail) : null;
      if (fromDetail && fromDetail.length <= 280 && !/^\s*[\[{]/.test(fromDetail)) {
        return fromDetail;
      }
      return 'Please check the workspace name and description and try again.';
    }
    if (status === 429) {
      return 'Too many requests. Please wait a moment and try again.';
    }
    if (status != null && status >= 500) {
      return 'Something went wrong on our end. Please try again in a few minutes.';
    }
    if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message)) {
      return 'The request took too long. Please try again.';
    }
    if (!error.response) {
      return "We couldn't reach the server. Check your connection and try again.";
    }
    return WORKSPACE_CREATE_GENERIC;
  }

  if (error instanceof Error) {
    const m = error.message;
    if (/must be authenticated/i.test(m)) {
      return 'Please sign in again to create a workspace.';
    }
    if (/session not found|log in again/i.test(m)) {
      return 'Please sign in again to create a workspace.';
    }
    return WORKSPACE_CREATE_GENERIC;
  }

  return WORKSPACE_CREATE_GENERIC;
}

/** Backend workspace response shape (snake_case) */
export interface BackendWorkspace {
  id: string;
  name: string;
  description?: string;
  user_id: string;
  created_at?: string;
  updated_at?: string;
}

/** Frontend workspace shape (camelCase) */
export interface Workspace {
  id: string;
  name: string;
  description?: string;
  userId: string;
  createdAt?: string;
  updatedAt?: string;
}

function mapBackendToWorkspace(w: BackendWorkspace): Workspace {
  return {
    id: w.id,
    name: w.name,
    description: w.description,
    userId: w.user_id,
    createdAt: w.created_at,
    updatedAt: w.updated_at,
  };
}

/**
 * Fetch all workspaces for the current user.
 * @returns List of workspaces (camelCase)
 * @throws On non-200 response or API errors
 */
export async function fetchWorkspaces(): Promise<Workspace[]> {
  const response = await apiClient.get<{ workspaces: BackendWorkspace[] }>('/api/v1/workspaces');

  if (response.status !== 200) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }

  const backendWorkspaces = response.data?.workspaces ?? [];
  return backendWorkspaces.map(mapBackendToWorkspace);
}

/**
 * Create a new workspace.
 * @param name - Workspace name
 * @param userId - Internal backend user ID
 * @param description - Optional description
 * @returns Created workspace (camelCase)
 * @throws On non-201 response or API errors
 */
export async function createWorkspace(
  name: string,
  userId: string,
  description?: string,
): Promise<Workspace> {
  const response = await apiClient.post<{ workspace: BackendWorkspace }>('/api/v1/workspaces', {
    name,
    description,
    userId,
  });

  if (response.status !== 201) {
    throw new Error(`Failed to create workspace: ${response.statusText}`);
  }

  const workspaceData = response.data?.workspace;
  if (!workspaceData) {
    throw new Error('Invalid create workspace response: missing workspace');
  }

  return mapBackendToWorkspace(workspaceData);
}

export type WorkspaceUpdatePayload = {
  name?: string;
  description?: string | null;
};

/**
 * Update workspace name and/or description.
 * @param workspaceId - Workspace ID
 * @param updates - Fields to change (omit to leave unchanged; description: null clears it)
 */
export async function updateWorkspace(
  workspaceId: string,
  updates: WorkspaceUpdatePayload,
): Promise<Workspace> {
  const response = await apiClient.patch<{ workspace: BackendWorkspace }>(
    `/api/v1/workspaces/${encodeURIComponent(workspaceId)}`,
    updates,
  );

  if (response.status !== 200) {
    throw new Error(`Failed to update workspace: ${response.statusText}`);
  }

  const workspaceData = response.data?.workspace;
  if (!workspaceData) {
    throw new Error('Invalid update workspace response: missing workspace');
  }

  return mapBackendToWorkspace(workspaceData);
}

/**
 * Delete a workspace owned by the current user.
 */
export async function deleteWorkspace(workspaceId: string): Promise<void> {
  const response = await apiClient.delete(`/api/v1/workspaces/${encodeURIComponent(workspaceId)}`);

  if (response.status < 200 || response.status >= 300) {
    throw new Error(`Failed to delete workspace: ${response.status} ${response.statusText}`);
  }
}
